import { AxiosError } from 'axios'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { ServerValidationError } from '@/api/shared/types'
import { cn } from '@/app/utils'
import { UploadFiles } from '@/components/features/upload-files'
import { UploadedFileType } from '@/components/shared/file-uploader'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'
import { ALL_ALLOWED_EXTENSIONS } from '@/utils/file'

import { CommonRequestFormData } from '../model/schemas'

type Props = {
  className?: string
  disabled?: boolean
  isUploading?: boolean
  setUploadProgress?: (_: number) => void
  requestError?: AxiosError<ServerValidationError> | null
}

const RequestFileUploaderFormBlock: FC<Props> = ({
  className,
  disabled,
  isUploading,
  setUploadProgress,
  requestError
}) => {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue
  } = useFormContext<CommonRequestFormData>()

  const handleFilesUpload = useCallback(
    (files: UploadedFileType[]) => {
      clearErrors('files')
      setValue('files', files, { shouldDirty: true })
    },
    [clearErrors, setValue]
  )

  const applyFilesServerError = useCallback(() => {
    const serverErrors = requestError?.response?.data?.errors
    if (!serverErrors?.length) {
      return
    }

    const filesError = serverErrors.find(error => error.field === 'files')

    if (filesError?.message && errors.files?.message !== filesError.message) {
      setError('files', {
        type: 'server',
        message: filesError.message
      })
    }
  }, [errors.files?.message, requestError, setError])

  useEffect(() => {
    applyFilesServerError()
  }, [applyFilesServerError])

  const filesTotalSizeExceeded = useMemo(
    () => errors.files?.type === 'totalSize',
    [errors.files]
  )
  const hasFilesError = Boolean(errors.files?.message)
  const isServerFilesError = errors.files?.type === 'server'

  return (
    <div className={cn('p-32 pt-0 lg:p-16', className)}>
      <Controller
        name="files"
        control={control}
        render={({ field: { value } }) => (
          <UploadFiles
            accept={ALL_ALLOWED_EXTENSIONS}
            className="mb-8"
            defaultFiles={value}
            onUpload={handleFilesUpload}
            disabled={disabled}
            isUploading={isUploading}
            setUploadProgress={setUploadProgress}
          />
        )}
      />
      <AppInputMessage
        message={
          hasFilesError
            ? errors.files?.message
            : 'Можно прикрепить не более 10 файлов, общим объёмом не более 200 Мб'
        }
        messageClassName={
          filesTotalSizeExceeded || isServerFilesError
            ? 'text-red-70'
            : 'text-black/50'
        }
        iconClassName={
          filesTotalSizeExceeded || isServerFilesError
            ? 'text-red-70'
            : 'hidden'
        }
      />
    </div>
  )
}

export default RequestFileUploaderFormBlock
